import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useContent } from "@/lib/content";
import { useLocation } from "react-router-dom";
import VideoEmbed from "@/components/places/VideoEmbed";

/**
 * Time-based popups configured via CMS (cms_kv.popups). Each popup has:
 *   { id, enabled, delay_seconds, title, body, image, cta_label, cta_url,
 *     once_per_session (bool), pages ("all" | "home" | comma-list of paths) }
 *
 * Popups fire once per browser session (localStorage) so the visitor is not spammed
 * on every navigation. Only ONE popup can be visible at a time — if two would fire
 * near each other, later ones are queued and shown when the previous is closed.
 */

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

const STORAGE_KEY = "places_popups_seen_v1";

function loadSeen() {
  try { return new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]")); }
  catch { return new Set(); }
}
function markSeen(id) {
  try {
    const s = loadSeen();
    s.add(id);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...s]));
  } catch { /* sessionStorage disabled — silently skip */ }
}

function shouldShowOnPath(popup, pathname) {
  const p = popup.pages || "all";
  if (p === "all") return true;
  const list = String(p).split(",").map((s) => s.trim()).filter(Boolean);
  return list.some((entry) => entry === "home" ? pathname === "/" : pathname.startsWith(entry));
}

export default function Popups() {
  const { popups: POPUPS = [] } = useContent();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(null); // active popup object
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (!Array.isArray(POPUPS) || POPUPS.length === 0) return;
    const seen = loadSeen();
    const timers = [];
    const eligible = POPUPS
      .filter((p) => p && p.enabled !== false)
      .filter((p) => !seen.has(p.id))
      .filter((p) => shouldShowOnPath(p, pathname))
      .sort((a, b) => (Number(a.delay_seconds) || 0) - (Number(b.delay_seconds) || 0));

    if (!eligible.length) return;

    eligible.forEach((p) => {
      const delay = Math.max(0, Number(p.delay_seconds) || 0) * 1000;
      const timer = setTimeout(() => {
        setVisible((cur) => {
          if (cur) {
            setQueue((q) => [...q, p]);
            return cur;
          }
          return p;
        });
      }, delay);
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
    // setVisible/setQueue are stable useState setters; POPUPS + pathname are the only real deps.
  }, [POPUPS, pathname]);

  const close = () => {
    if (visible) markSeen(visible.id);
    setVisible(null);
    // Show next queued popup after a short pause
    setTimeout(() => {
      setQueue((q) => {
        if (q.length === 0) return q;
        const [next, ...rest] = q;
        setVisible(next);
        return rest;
      });
    }, 400);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid={`popup-${visible.id}`}
        >
          {/* Backdrop — leve escurecimento (não bloqueia totalmente o conteúdo) */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            data-testid="popup-backdrop"
          />
          {/* Card */}
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            data-testid="popup-card"
          >
            <button
              onClick={close}
              data-testid="popup-close"
              aria-label="Fechar"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {visible.media_type === "video" && visible.video_url ? (
              <div className="w-full aspect-[16/9] overflow-hidden bg-neutral-100" data-testid="popup-video">
                <VideoEmbed src={visible.video_url} title={visible.title || "popup"} aspect="aspect-video" autoplay muted controls />
              </div>
            ) : visible.image && (
              <div className="w-full aspect-[16/9] overflow-hidden bg-neutral-100">
                <img src={resolveSrc(visible.image)} alt="" className="w-full h-full object-cover" />
              </div>
            )}

            <div className="p-6 md:p-8">
              {visible.eyebrow && (
                <div className="text-xs uppercase tracking-[0.2em] text-accent-orange font-bold mb-2" data-testid="popup-eyebrow">
                  {visible.eyebrow}
                </div>
              )}
              {visible.title && (
                <h3 className="font-display text-2xl md:text-3xl leading-tight text-black" data-testid="popup-title">
                  {visible.title}
                </h3>
              )}
              {visible.body && (
                <p className="text-sm md:text-base text-neutral-600 mt-3 leading-relaxed" data-testid="popup-body">
                  {visible.body}
                </p>
              )}
              {visible.cta_label && visible.cta_url && (
                <a
                  href={visible.cta_url}
                  target={visible.cta_url.startsWith("http") ? "_blank" : "_self"}
                  rel={visible.cta_url.startsWith("http") ? "noopener noreferrer" : undefined}
                  onClick={() => markSeen(visible.id)}
                  data-testid="popup-cta"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold text-sm uppercase tracking-wide transition-all hover:scale-[1.02] shadow-lg"
                  style={{
                    background: visible.cta_bg || "#D4FF00",
                    color: visible.cta_color || "#000",
                  }}
                >
                  {visible.cta_label}
                </a>
              )}
              {visible.footnote && (
                <div className="mt-3 text-xs text-neutral-400 text-center" data-testid="popup-footnote">
                  {visible.footnote}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
